import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, withModifiers as _withModifiers, withKeys as _withKeys, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "row justify-center q-pa-md"
}
const _hoisted_2 = ["aria-label", "onKeydown"]

import { computed, ref, onMounted, watch } from 'vue'
import type { QInput } from 'quasar'
import PaypalButton from '@/components/PaypalButton.vue'

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'PayComponent',
  props: {
    appointmentID: {},
    duration: {},
    time: {},
    sessionType: {},
    restBetrag: {},
    startDate: {},
    studioMalbert: { type: Boolean },
    studioDortmund: { type: Boolean }
  },
  emits: ["update:isLoading"],
  setup(__props: any, { emit: __emit }) {

const props = __props

/* eslint-disable */
const emit = __emit

const numberName = ref('')
const contactInputRef = ref<QInput | null>(null)
const orderContextReady = ref(false)
const isPreparingOrderContext = ref(false)
const orderContextError = ref<string | null>(null)
const isAppointmentUnavailable = ref(false)

// 🌍 Sprache anhand der URL bestimmen
const path = window.location.pathname
const lang = path.startsWith('/en/') ? 'en' : 'de'

// Dynamische Fehlermeldung
const requiredMessage = computed(() =>
  lang === 'de'
    ? 'Deine Handynummer oder deinen Telegram-Namen eingeben'
    : 'Enter your cell number or Telegram name'
)

// Computeds
const needsContact = computed(() => props.sessionType === 'o')
const missingContact = computed(() => numberName.value.length < 3)
const isGuardActive = computed(() => needsContact.value && missingContact.value)
const canRenderStripeButton = computed(() => orderContextReady.value && !isPreparingOrderContext.value && !orderContextError.value && !isAppointmentUnavailable.value)
const isOrderContextBlocked = computed(() => !canRenderStripeButton.value)
const isPaymentBlocked = computed(() => isGuardActive.value || isOrderContextBlocked.value)
const reference = computed(() => `${props.appointmentID}__${props.time}`)

const sessionTyp = computed(() => {
  if (props.sessionType === 'o') return 'Plauderstunde (online)'
  if (props.sessionType === 'p') return 'Plauderstunde'
  if (props.sessionType === 's' && (props.studioMalbert || props.studioDortmund)) return 'Session (Studio Malbert)'
  if (props.sessionType === 's') return 'Session'
  return props.sessionType
})

const guardMessage = computed(() => {
  if (isGuardActive.value) return requiredMessage.value
  if (orderContextError.value) return orderContextError.value
  return lang === 'de'
    ? 'Zahlung wird vorbereitet'
    : 'Payment is being prepared'
})

const price = computed<number>(() => {
  if (props.sessionType === 'o') {
    if (props.restBetrag == null || props.restBetrag == 50) return 100
    return 150
  }
  return 50
})

onMounted(() => {
  void ensureOrderContext()
})

watch(
  () => [
    props.appointmentID,
    props.time,
    props.startDate?.getTime(),
    props.duration,
    props.sessionType,
    props.restBetrag,
    props.studioMalbert,
    props.studioDortmund,
  ],
  () => {
    void ensureOrderContext()
  }
)

// Overlay-Klick → Fehler anzeigen + Scroll
function handleGuardClick() {
  if (isGuardActive.value) {
    contactInputRef.value?.validate()
    const el = contactInputRef.value?.$el as HTMLElement | undefined
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    requestAnimationFrame(() => contactInputRef.value?.focus())
    return
  }

  if (orderContextError.value) {
    void ensureOrderContext()
  }
}

async function ensureOrderContext(extraPayload: Record<string, unknown> = {}) {
  orderContextReady.value = false
  orderContextError.value = null
  isAppointmentUnavailable.value = false

  if (!props.startDate || Number.isNaN(props.startDate.getTime())) {
    orderContextError.value = lang === 'de'
      ? 'Der Termin konnte nicht vorbereitet werden. Bitte wähle den Termin neu aus.'
      : 'The appointment could not be prepared. Please select the appointment again.'
    return
  }

  const endDate = new Date(props.startDate.getTime() + props.duration * 60_000)
  if (Number.isNaN(endDate.getTime())) {
    orderContextError.value = lang === 'de'
      ? 'Die Termindauer ist ungültig. Bitte wähle den Termin neu aus.'
      : 'The appointment duration is invalid. Please select the appointment again.'
    return
  }

  const payload = {
    reference: reference.value,
    appointmentId: props.appointmentID,
    startDate: props.startDate.toISOString(),
    endDate: endDate.toISOString(),
    restBetrag: props.restBetrag,
    sessionTyp: sessionTyp.value,
    studioMalbert: Boolean(props.studioMalbert || props.studioDortmund),
    studioDortmund: Boolean(props.studioMalbert || props.studioDortmund),
    studio: props.studioMalbert || props.studioDortmund ? 'dortmund' : undefined,
    preserveExisting: true,
    ...extraPayload,
  }

  isPreparingOrderContext.value = true
  try {
    const response = await fetch('https://setordernote-3uz45qef3a-ey.a.run.app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      if (response.status === 409 && data?.code === 'appointment_unavailable') {
        isAppointmentUnavailable.value = true
        orderContextError.value = lang === 'de'
          ? 'Dieser Termin ist leider nicht mehr verfügbar. Bitte wähle einen neuen Termin.'
          : 'This appointment is no longer available. Please select a new appointment.'
        return
      }

      if (response.status === 400 && data?.code === 'invalid_appointment_time') {
        orderContextError.value = lang === 'de'
          ? 'Die Termindaten sind ungültig. Bitte wähle den Termin neu aus.'
          : 'The appointment details are invalid. Please select the appointment again.'
        return
      }

      throw new Error(`setOrderNote failed: ${response.status}`)
    }

    orderContextReady.value = true
  } catch(e) {
    console.log(e)
    orderContextError.value = lang === 'de'
      ? 'Die Zahlung konnte nicht vorbereitet werden. Bitte lade die Seite neu.'
      : 'Payment could not be prepared. Please reload the page.'
  } finally {
    isPreparingOrderContext.value = false
  }
}

async function updateDB() {
  if (isGuardActive.value) {
    return
  }
  await ensureOrderContext({ whatsappTelegram: numberName.value.trim() })
}


return (_ctx: any,_cache: any) => {
  const _component_q_input = _resolveComponent("q-input")!
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_banner = _resolveComponent("q-banner")!
  const _component_q_spinner = _resolveComponent("q-spinner")!
  const _component_stripe_buy_button = _resolveComponent("stripe-buy-button")!

  return (_openBlock(), _createElementBlock("div", null, [
    (__props.sessionType === 'o')
      ? (_openBlock(), _createBlock(_component_q_card, {
          key: 0,
          dark: "",
          flat: "",
          class: "q-mb-lg"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_card_section, null, {
              default: _withCtx(() => [
                _createVNode(_component_q_input, {
                  ref_key: "contactInputRef",
                  ref: contactInputRef,
                  dark: "",
                  filled: "",
                  modelValue: numberName.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((numberName).value = $event)),
                  label: _unref(lang) === 'de' ? 'WhatsApp/Telegram' : 'WhatsApp/Telegram',
                  rules: [
            (val) => !!(val && val.trim().length >= 3) || requiredMessage.value
          ],
                  hint: _unref(lang) === 'de' ? 'Handynummer (für WhatsApp) oder Telegram Namen eingeben' : 'Enter cell phone number (for WhatsApp) or Telegram name',
                  placeholder: "+49 1525 2714335",
                  "lazy-rules": "",
                  onBlur: _cache[1] || (_cache[1] = ($event: any) => (updateDB())),
                  onKeydown: _cache[2] || (_cache[2] = _withKeys(_withModifiers(() => {}, ["prevent"]), ["enter"]))
                }, null, 8, ["modelValue", "label", "rules", "hint"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (orderContextError.value)
      ? (_openBlock(), _createBlock(_component_q_banner, {
          key: 1,
          dark: "",
          rounded: "",
          dense: "",
          class: "q-mb-lg"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(orderContextError.value), 1)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (!isAppointmentUnavailable.value && !orderContextError.value)
      ? (_openBlock(), _createBlock(_component_q_card, {
          key: 2,
          dark: "",
          flat: "",
          class: "relative"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_card_section, null, {
              default: _withCtx(() => [
                (isPreparingOrderContext.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                      _createVNode(_component_q_spinner, {
                        color: "primary",
                        size: "32px"
                      })
                    ]))
                  : _createCommentVNode("", true),
                (canRenderStripeButton.value && price.value === 100)
                  ? (_openBlock(), _createBlock(_component_stripe_buy_button, {
                      key: 1,
                      "buy-button-id": "buy_btn_1SQNsWLLLx7K96FRW7XGX9OR",
                      "publishable-key": "pk_live_51SOCSrLLLx7K96FRnS3yjbJcYwg6F4ZxaeaL8zMStPiw5kR97007WkchG9tK1PMtrIHXwbdKlO33YSMvtkynXKbh00qxF4z1jq",
                      "client-reference-id": reference.value,
                      class: _normalizeClass({'btn-disable': isPaymentBlocked.value})
                    }, null, 8, ["client-reference-id", "class"]))
                  : _createCommentVNode("", true),
                (canRenderStripeButton.value && price.value === 150)
                  ? (_openBlock(), _createBlock(_component_stripe_buy_button, {
                      key: 2,
                      "buy-button-id": "buy_btn_1SQNuZLLLx7K96FRJ85DcG2a",
                      "publishable-key": "pk_live_51SOCSrLLLx7K96FRnS3yjbJcYwg6F4ZxaeaL8zMStPiw5kR97007WkchG9tK1PMtrIHXwbdKlO33YSMvtkynXKbh00qxF4z1jq",
                      "client-reference-id": reference.value,
                      class: _normalizeClass({'btn-disable': isPaymentBlocked.value})
                    }, null, 8, ["client-reference-id", "class"]))
                  : _createCommentVNode("", true),
                (canRenderStripeButton.value && price.value === 50)
                  ? (_openBlock(), _createBlock(_component_stripe_buy_button, {
                      key: 3,
                      "buy-button-id": "buy_btn_1SOCmbLLLx7K96FRCLXMuffI",
                      "publishable-key": "pk_live_51SOCSrLLLx7K96FRnS3yjbJcYwg6F4ZxaeaL8zMStPiw5kR97007WkchG9tK1PMtrIHXwbdKlO33YSMvtkynXKbh00qxF4z1jq",
                      "client-reference-id": reference.value,
                      class: _normalizeClass({'btn-disable': isPaymentBlocked.value})
                    }, null, 8, ["client-reference-id", "class"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            false
              ? (_openBlock(), _createBlock(_component_q_card_section, { key: 0 }, {
                  default: _withCtx(() => [
                    _createVNode(PaypalButton, {
                      amount: __props.sessionType === 'o' ? 50 + (__props.restBetrag != null ? __props.restBetrag : 50) : 50,
                      reference: reference.value,
                      class: _normalizeClass({'btn-disable': isPaymentBlocked.value}),
                      "onUpdate:isLoading": _cache[3] || (_cache[3] = (val) => emit('update:isLoading', val))
                    }, null, 8, ["amount", "reference", "class"])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (isPaymentBlocked.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: "click-guard",
                  role: "button",
                  tabindex: "0",
                  "aria-label": guardMessage.value,
                  onClick: handleGuardClick,
                  onKeydown: [
                    _withKeys(_withModifiers(handleGuardClick, ["prevent"]), ["enter"]),
                    _withKeys(_withModifiers(handleGuardClick, ["prevent"]), ["space"])
                  ]
                }, null, 40, _hoisted_2))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})